/**
 * Group J — onboarding (`backend/api/v1/onboarding.py`).
 *
 *   GET  /v1/onboarding               where this account's signup stands
 *   PUT  /v1/onboarding/community     claim another community
 *   POST /v1/onboarding/verification  "I've let you in" — look again now
 *   POST /v1/onboarding/audit         re-run the claim's audit, in another language
 *   POST /v1/onboarding/join          join with the answers the owner approved
 *
 * Signup is a pasted community link, then an email and a password. Everything after that
 * runs without the owner — Sideguide's Skool account joins the community, the ingest runs
 * — and this resource is how the Discovery page reports it.
 *
 * NOT CACHED: every caller is either a poll or a step transition, and the point of both is
 * the current state. The account-level bootstrap (`@api/v1/me`) is the cached one.
 */

import { apiGet, apiPost, apiPut } from './http';

/** `domain.accounts.onboarding_stage` — the same value `/v1/me` reports. Every stage
 *  short of `ready` routes to the Discovery page. */
export type OnboardingStage = 'ready' | 'indexing' | 'connecting' | 'no_community';

/** How far Sideguide's Skool account has got into the community
 *  (`domain.accounts.ACCESS_*`). Null before the first try. */
export type OnboardingAccess =
  | 'member'
  | 'pending_approval'
  /** The community screens newcomers with questions; `join_form` holds them, drafted. */
  | 'needs_answers'
  | 'needs_invite'
  | 'needs_attention';

/** One question a community asks before letting somebody in, with the answer going with
 *  it — ours as a draft, the owner's once they have edited it. A blank answer is one we
 *  would not write on their behalf. */
export interface JoinQuestion {
  question: string;
  type: 'text' | 'options' | 'email' | string;
  /** Present for `options`; the answer must be one of them, copied exactly. */
  options: string[];
  answer: string;
}

export interface OnboardingCommunity {
  slug: string;
  name: string;
  url: string;
  logo_url: string;
  total_members: number;
}

export interface IndexingStep {
  /** The onboarding flow's job name — the page decides what to call it. */
  job: string;
  /** `pending` until its job starts, then that job's ledger status. */
  status: string;
}

export interface Indexing {
  state: 'waiting' | 'queued' | 'running' | 'done' | 'failed';
  /** Imports that will run before this one. */
  ahead: number;
  steps: IndexingStep[];
}

export interface Onboarding {
  stage: OnboardingStage;
  /** The Skool account a paid community's owner invites. */
  invite_email: string;
  /** The community being connected or, once granted, the newest one held. */
  community: OnboardingCommunity | null;
  /** True once the claim is granted — the account HOLDS the community rather than still
   *  waiting to be let in. The backend derives it from `pending_skool_url` being cleared,
   *  so it is the one field that distinguishes "setting up" from "set up". */
  community_held: boolean;
  access: OnboardingAccess | null;
  /** Present once Sideguide is inside the community. */
  indexing: Indexing | null;
  /** The join questions while `access` is `needs_answers`, and afterwards the record of
   *  what was sent. */
  join_form: JoinQuestion[] | null;
  /** The newest audit of `community` belonging to this account. */
  audit_id: number | null;
}

export function fetchOnboarding(): Promise<Onboarding> {
  return apiGet<Onboarding>('/onboarding');
}

/**
 * Claim another community. Starts its audit and Sideguide's join, as signup does.
 *
 * Throws `CodedApiError` with `code`:
 *   * `validation_failed` (422) — not a Skool community link;
 *   * `community_not_found` (404) — Skool has no community there;
 *   * `community_already_claimed` (409) — another account holds or has claimed it.
 */
export function claimCommunity(url: string): Promise<Onboarding> {
  return apiPut<Onboarding>('/onboarding/community', { url });
}

/**
 * Join the claimed community with the answers the owner approved — one per question, in
 * the order `join_form` gave them. Nothing is sent to the community until this is called.
 *
 * Throws `CodedApiError` `validation_failed` (422) when the answers do not match the form
 * (a stale tab), `upstream_unavailable` (502) when Skool does not answer.
 */
export function submitJoinAnswers(answers: string[]): Promise<Onboarding> {
  return apiPost<Onboarding>('/onboarding/join', { answers });
}

/** The owner says they have invited or approved Sideguide, or wants a failed join or a
 *  missing audit tried again — the page's one "try again" while a claim is in flight.
 *  Throws `upstream_unavailable` (502) when Skool does not answer. */
/**
 * Audit the community being claimed again, optionally in a different language.
 *
 * Distinct from `confirmAccess`, which retries the JOIN and only starts an audit when
 * there is none. This always starts a fresh run, because "measure it in German instead"
 * is a new question about the same community rather than a retry of the old answer.
 *
 * It exists because a claim has nowhere else to ask: the per-community audit endpoint
 * needs a community the account already holds, and during setup it holds none.
 */
export function rerunSignupAudit(language?: string | null): Promise<Onboarding> {
  return apiPost<Onboarding>('/onboarding/audit', language ? { language } : undefined);
}

export function confirmAccess(): Promise<Onboarding> {
  return apiPost<Onboarding>('/onboarding/verification');
}
